import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Aperture, Loader2, CheckCircle2 } from "lucide-react";

const AUTH_IMG = "https://images.pexels.com/photos/19923619/pexels-photo-19923619.jpeg";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block">
        <img src={AUTH_IMG} alt="photographer" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/60 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-display text-4xl leading-tight text-white">A fresh start.</h2>
          <p className="text-neutral-300 mt-3 max-w-md">Choose a new password and get back to creating.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2.5 mb-10">
            <Aperture className="h-7 w-7 text-amber-glow" strokeWidth={1.5} />
            <span className="font-display text-2xl">Aevolux</span>
          </div>

          {done ? (
            <div data-testid="reset-success">
              <CheckCircle2 className="h-10 w-10 text-amber-glow mb-5" strokeWidth={1.5} />
              <h1 className="font-display text-3xl mb-3">Password updated</h1>
              <p className="text-neutral-400">Redirecting you to sign in…</p>
            </div>
          ) : !token ? (
            <div data-testid="reset-no-token">
              <h1 className="font-display text-3xl mb-3">Invalid link</h1>
              <p className="text-neutral-400">This reset link is missing its token. Please request a new one.</p>
              <Link to="/forgot-password" className="inline-block mt-8 text-amber-glow hover:underline">Request reset link</Link>
            </div>
          ) : (
            <>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-neutral-500">Almost there</p>
              <h1 className="font-display text-4xl mt-2 mb-8">New password</h1>

              {error && (
                <div data-testid="reset-error" className="mb-5 text-sm text-red-400 bg-red-950/40 border border-red-900/50 px-4 py-3 rounded-sm">
                  {error}
                </div>
              )}

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-xs tracking-[0.15em] uppercase text-neutral-400">New password</label>
                  <Input
                    data-testid="reset-password-input"
                    type="password" required value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }}
                    className="mt-2 bg-[#121212] border-white/10 h-12 rounded-sm focus-visible:ring-amber-glow"
                    placeholder="At least 6 characters"
                  />
                </div>
                <div>
                  <label className="text-xs tracking-[0.15em] uppercase text-neutral-400">Confirm password</label>
                  <Input
                    data-testid="reset-confirm-input"
                    type="password" required value={confirm} onChange={(e) => { setConfirm(e.target.value); setError(""); }}
                    className="mt-2 bg-[#121212] border-white/10 h-12 rounded-sm focus-visible:ring-amber-glow"
                    placeholder="Re-enter password"
                  />
                </div>
                <Button
                  data-testid="reset-submit-button"
                  type="submit" disabled={loading}
                  className="w-full h-12 bg-white text-black font-semibold hover:bg-neutral-200 rounded-sm"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
